/**
 * Supabase client wrapper
 * Manages client initialization, updates, and state
 */

import { createClient } from '@supabase/supabase-js';
import type { SupabaseClient } from '@supabase/supabase-js';
// Remove the circular dependency by removing this import
// import { getSafeClient } from './safeClient';
import { getSupabaseClient as getSecureSupabaseClient, getEdgeFunctionBaseUrl, fetchSupabaseCredentials } from '../../lib/env';

// Global client reference - using var to allow reassignment
var client: SupabaseClient | null = null;
let isInitialized = false;

// Track initialization state for better error handling
const initState = {
  attemptCount: 0,
  lastAttempt: 0,
  url: '',
  inProgress: false
};

/**
 * Creates a new Supabase client instance
 * This function expects valid credentials
 */
function createSupabaseClient(supabaseUrl: string, supabaseAnonKey: string): SupabaseClient {
  try {
    // Basic validation
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Missing required Supabase credentials');
    }
    
    // Don't create a client with empty credentials
    if (supabaseUrl === '' || supabaseAnonKey === '') {
      throw new Error('Empty Supabase credentials provided');
    }

    // Log that we're creating a new instance
    console.log('Creating new Supabase client instance');
    
    return createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: false, // We'll handle token refresh ourselves
        persistSession: true,    // Session is persisted in storage
      }
    });
  } catch (error) {
    console.error('Error creating Supabase client:', error);
    throw error;
  }
}

/**
 * Updates the Supabase client with new credentials
 * Useful when credentials become available after initial load
 */
export function updateSupabaseClient(url: string, key: string): SupabaseClient {
  // Log basic info about the update (without exposing full key)
  console.log(`Updating Supabase client with URL: ${url} and key: ${key ? (key.substring(0, 3) + '...' + key.slice(-3)) : 'none'}`);
  
  // Track initialization state
  initState.attemptCount++;
  initState.lastAttempt = Date.now();
  initState.url = url;
  initState.inProgress = true;
  
  try {
    // Create a new client
    client = createSupabaseClient(url, key);
    isInitialized = true;
    
    console.log('Supabase client successfully updated');
    return client;
  } catch (error) {
    console.error('Failed to update Supabase client:', error);
    isInitialized = false;
    throw error;
  } finally {
    initState.inProgress = false;
  }
}

/**
 * Gets the Supabase client instance, creates one if necessary
 * @returns The Supabase client
 * @throws Error if the client is not initialized
 */
export async function getSupabaseClient(): Promise<SupabaseClient> {
  // Check if we have an initialized client
  if (client && isInitialized) {
    return client;
  }
  
  // Initialize if not already done
  return initializeClient();
}

/**
 * Checks if the Supabase client is properly initialized
 */
export function isSupabaseInitialized(): boolean {
  return isInitialized && client !== null;
}

/**
 * Gets current initialization state for debugging
 */
export function getInitializationState() {
  return {
    ...initState,
    isInitialized,
    lastAttemptTime: initState.lastAttempt ? new Date(initState.lastAttempt).toISOString() : 'never',
    hasClient: client !== null
  };
}

/**
 * Test the Supabase connection
 * This is useful to verify that credentials are working
 */
export async function testConnection(): Promise<boolean> {
  try {
    if (!isInitialized || !client) {
      console.error('Cannot test connection - Supabase client not initialized');
      return false;
    }
    
    // Test with a simple query to the offers table instead of _test
    const { data, error } = await client.from('offers').select('id').limit(1);
    
    if (error) {
      console.error('Connection test failed:', error);
      return false;
    }
    
    console.log('Connection test successful');
    return true;
  } catch (error) {
    console.error('Error testing connection:', error);
    return false;
  }
}

/**
 * Client-side token refresh
 * Call this when you detect a token needs to be refreshed
 */
export const refreshToken = async (): Promise<string | null> => {
  try {
    if (!client) {
      throw new Error('Supabase client not initialized');
    }
    
    console.log('Attempting to refresh Supabase token');
    
    // Instead of using the built-in refresh, get a new token from our secure Edge Function
    try {
      const client = await getSupabaseClient();
      const { data: { session } } = await client.auth.getSession();
      
      if (!session) {
        throw new Error('No session available');
      }

      // Get the current URL from the stored session
      const storedSession = sessionStorage.getItem('supabase_session');
      const currentUrl = storedSession ? JSON.parse(storedSession).url : null;
      
      if (!currentUrl) {
        throw new Error('No stored session URL found');
      }
      
      // Store refreshed session
      try {
        sessionStorage.setItem('supabase_session', JSON.stringify({
          url: currentUrl,
          token: session.access_token,
          expires_at: session.expires_at
        }));
        
        console.log('Token refreshed and stored successfully');
      } catch (e) {
        console.warn('Error storing refreshed session', e);
      }
      
      // Update the client with the new token
      updateSupabaseClient(currentUrl, session.access_token);
      
      return session.access_token;
    } catch (error) {
      // If edge function fails, try native refresh as fallback
      console.warn('Edge function token refresh failed, trying built-in refresh:', error);
      
      const { data, error: refreshError } = await client.auth.refreshSession();
      
      if (refreshError) {
        throw refreshError;
      }
      
      if (!data.session) {
        throw new Error('No session in refresh response');
      }
      
      // Store refreshed session
      try {
        const storedSession = sessionStorage.getItem('supabase_session');
        const url = storedSession ? JSON.parse(storedSession).url : initState.url;
        
        sessionStorage.setItem('supabase_session', JSON.stringify({
          url: url,
          token: data.session.access_token,
          expires_at: data.session.expires_at
        }));
        
        console.log('Token refreshed via built-in refresh and stored successfully');
      } catch (e) {
        console.warn('Error storing refreshed session', e);
      }
      
      return data.session.access_token;
    }
  } catch (error) {
    console.error('All token refresh methods failed:', error);
    return null;
  }
};

/**
 * Monitor token expiration and refresh before it expires
 */
export const setupTokenExpirationMonitor = (expiresAt?: string) => {
  if (!expiresAt) {
    // Try to get from sessionStorage
    try {
      const storedSession = sessionStorage.getItem('supabase_session');
      if (storedSession) {
        const { expires_at } = JSON.parse(storedSession);
        expiresAt = expires_at;
      }
    } catch (e) {
      console.warn('Error reading session expiration from storage', e);
    }
  }
  
  if (!expiresAt) {
    console.warn('No token expiration time available, cannot set up monitor');
    return;
  }
  
  const expiryTime = new Date(expiresAt).getTime();
  const now = new Date().getTime();
  const timeUntilExpiry = expiryTime - now;
  
  // Refresh 5 minutes before expiration
  if (timeUntilExpiry > 0) {
    const refreshDelay = Math.max(timeUntilExpiry - (5 * 60 * 1000), 0);
    
    console.log(`Setting up token refresh in ${refreshDelay / 1000} seconds`);
    
    setTimeout(async () => {
      try {
        const newToken = await refreshToken();
        if (newToken) {
          // Set up the next refresh
          setupTokenExpirationMonitor();
        }
      } catch (error) {
        console.error('Failed to refresh token before expiration', error);
      }
    }, refreshDelay);
  } else {
    // Token is already expired, refresh immediately
    console.warn('Token is already expired, refreshing immediately');
    refreshToken().catch(error => {
      console.error('Failed to refresh expired token', error);
    });
  }
};

/**
 * Initializes the Supabase client with credentials from Edge Function
 * This is the secure way to get credentials without exposing them in the frontend
 */
export async function initializeWithSecureCredentials(): Promise<SupabaseClient> {
  try {
    console.log('[Supabase] Starting initialization with secure credentials...');
    console.log('[Supabase] Current environment:', {
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      pathname: window.location.pathname
    });
    
    initState.inProgress = true;
    initState.attemptCount++;
    initState.lastAttempt = Date.now();
    
    console.log('[Supabase] Fetching credentials from Edge Function...');
    console.log('[Supabase] Edge Function URL:', `${getEdgeFunctionBaseUrl()}/secure-client`);
    
    // Fetch credentials securely from Edge Function
    const { supabaseUrl, accessToken } = await fetchSupabaseCredentials();
    console.log('[Supabase] Credentials received from Edge Function');
    
    // Create new client with credentials
    const newClient = createSupabaseClient(supabaseUrl, accessToken);
    console.log('[Supabase] Client created with credentials');
    
    // Test the connection
    try {
      console.log('[Supabase] Testing connection...');
      const { data, error } = await newClient.from('offers').select('id').limit(1);
      if (error) {
        console.error('[Supabase] Connection test failed:', error);
      } else {
        console.log('[Supabase] Connection test successful');
      }
    } catch (error) {
      console.error('[Supabase] Connection test error:', error);
    }
    
    // Store the client
    console.log('[Supabase] Client created successfully, updating global state...');
    client = newClient;
    isInitialized = true;
    
    // Set up token expiration monitoring
    try {
      console.log('[Supabase] Setting up token expiration monitoring...');
      const storedSession = sessionStorage.getItem('supabase_session');
      if (storedSession) {
        const { expires_at } = JSON.parse(storedSession);
        console.log('[Supabase] Found existing session, setting up expiration monitor');
        setupTokenExpirationMonitor(expires_at);
      } else {
        console.log('[Supabase] No existing session found in storage');
      }
    } catch (e) {
      console.warn('[Supabase] Error setting up token expiration monitor:', e);
    }
    
    console.log('[Supabase] Client successfully initialized with secure credentials');
    return newClient;
  } catch (error) {
    console.error('[Supabase] Failed to initialize client with secure credentials:', error);
    if (error instanceof Error) {
      console.error('[Supabase] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
    }
    throw error;
  } finally {
    initState.inProgress = false;
    console.log('[Supabase] Initialization process completed');
  }
}

/**
 * Initialize the Supabase client using secure credentials
 */
export async function initializeClient(): Promise<SupabaseClient> {
  try {
    console.log('[Supabase] Starting client initialization...');
    
    // Initialize with secure credentials
    console.log('[Supabase] Initializing with secure credentials...');
    const newClient = await initializeWithSecureCredentials();
    console.log('[Supabase] Client initialized with secure credentials');
    
    // Test the connection
    const isConnected = await testConnection();
    if (!isConnected) {
      console.warn('[Supabase] Initial connection test failed, but continuing initialization');
    }
    
    return newClient;
  } catch (error) {
    console.error('[Supabase] Failed to initialize client:', error);
    if (error instanceof Error) {
      console.error('[Supabase] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
    }
    throw error;
  }
}

/**
 * Wait for Supabase client to be initialized
 * @param timeout Maximum time to wait in milliseconds
 * @returns Promise that resolves when client is initialized
 */
export const waitForInitialization = async (timeout = 10000): Promise<void> => {
  const startTime = Date.now();
  
  while (!isInitialized || !client) {
    if (Date.now() - startTime > timeout) {
      throw new Error('Timeout waiting for Supabase client initialization');
    }
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  // Test the connection
  try {
    console.log('[Supabase] Testing connection after initialization...');
    const { error } = await client.from('offers').select('id').limit(1);
    if (error) {
      console.error('[Supabase] Connection test failed:', error);
      throw error;
    }
    console.log('[Supabase] Connection test successful');
  } catch (error) {
    console.error('[Supabase] Connection test error:', error);
    throw error;
  }
};

// Remove the problematic circular dependency
// Initialize the Supabase client using the safe client pattern
console.log('[Supabase] Creating initial safe client...');
// export const supabase = getSafeClient();
console.log('[Supabase] Safe client created');

// Export the client for use in other files
// export default supabase;